import LanguageIcon from '@mui/icons-material/Language';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, ButtonBase, Divider, Typography } from '@mui/material';
import { capitalize } from 'lodash';
import { useGetIdentity, useTranslate } from 'react-admin';

import { Avatar } from '@sh4res/bo-common/components/Avatar';
import { ChipsList } from '@sh4res/bo-common/components/ChipsList';
import { CustomLocalesMenuButton } from '@sh4res/bo-common/components/CustomLocalesMenuButton';
import { StartProductTourButton } from '@sh4res/bo-common/components/StartProductTourButton';
import { UserMenuLogoutButton } from '@sh4res/bo-common/components/UserMenuLogoutButton';
import { isBoExternalIdentity } from '@sh4res/bo-common/utils/b2b_auth_role';

export const ProfilePopup = ({
  hideLogoutButton = false,
  showProductTour = false,
  startTourHandle,
  onOpenSettings,
}: {
  hideLogoutButton?: boolean;
  showProductTour?: boolean;
  startTourHandle?: () => void;
  /** shows a "Settings" row when set */
  onOpenSettings?: () => void;
}) => {
  const { data } = useGetIdentity();
  const translate = useTranslate();

  if (data === undefined) {
    return null;
  }
  if (showProductTour && !startTourHandle) {
    throw new Error('Missing startTourHandle prop in ProfilePopup component');
  }
  return (
    <Box
      sx={{
        width: 300,
        backgroundColor: 'white',
        overflow: 'hidden',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      }}>
      <Box
        sx={{
          borderBottom: '0.5px solid lightgrey',
        }}>
        <Box
          sx={{
            padding: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
          <Avatar
            sx={{ width: 60, height: 60 }}
            src={isBoExternalIdentity(data) ? undefined : data.picture_url}
            fullName={data.fullName}
          />
          <Typography variant="h6" component="p" sx={{ marginBottom: 0 }}>
            {data.fullName}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              marginTop: 0,
              color: 'text.secondary',
              width: '100%',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}>
            {data.email}
          </Typography>
          <Typography variant="body2" sx={{ marginTop: 0, color: 'text.secondary' }}>
            {isBoExternalIdentity(data) ? data.phone_number : undefined}
          </Typography>
        </Box>
        <Divider />
        <Box
          sx={{
            padding: 2,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingLeft: 4,
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ minWidth: '40px' }}>
              <ManageAccountsIcon />
            </Box>
            <Typography variant="body1" sx={{ marginLeft: 0 }}>
              {translate('resources.bo_common.profile_popup.role')}
            </Typography>
          </Box>

          <Box sx={{ paddingRight: 3 }}>
            <ChipsList
              list={data.roles
                .filter((role: string) => role.toLowerCase() !== 'default')
                .map((role: string) => {
                  const key = `resources.bo_common.b2b.companies.admins.choices.role.${role}`;
                  const value = translate(key);
                  return value === key ? capitalize(role) : value.split(' -')[0];
                })}
            />
          </Box>
        </Box>
        <Divider />
        <Box
          sx={{
            padding: 2,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingLeft: 4,
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ minWidth: '40px' }}>
              <LanguageIcon />
            </Box>
            <Typography variant="body1" sx={{ marginLeft: 0 }}>
              {translate('resources.bo_common.profile_popup.language')}
            </Typography>
          </Box>
          <CustomLocalesMenuButton testId="BoExternalProfilePopup_CustomLocalesMenuButton" />
        </Box>
        {onOpenSettings && (
          <>
            <Divider />
            <ButtonBase
              onClick={onOpenSettings}
              data-testid="BoExternalProfilePopup_SettingsButton"
              sx={{
                width: '100%',
                padding: 2,
                paddingLeft: 4,
                justifyContent: 'flex-start',
              }}>
              <Box sx={{ minWidth: '40px', display: 'flex' }}>
                <SettingsIcon />
              </Box>
              <Typography variant="body1" sx={{ marginLeft: 0 }}>
                {translate('resources.bo_common.profile_popup.settings')}
              </Typography>
            </ButtonBase>
          </>
        )}
        {showProductTour && startTourHandle && (
          <>
            <Divider />
            <Box sx={{ padding: 2 }}>
              <StartProductTourButton
                startTour={startTourHandle}
                data-testid="BoExternalProfilePopup_StartTourButton"
              />
            </Box>
          </>
        )}
        {!hideLogoutButton && (
          <>
            <Divider />
            <Box sx={{ padding: 2 }}>
              <UserMenuLogoutButton data-testid="BoExternalProfilePopup_LogoutButton" />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
